/**
 * Unity Global Care — Flemington + Princeton (UI 1 + Care Hub).
 * When either site is selected, client lists and AIP signals aggregate both locations.
 */

/** Corporate Unity parent — no client folders; never use for AIP change / folder fetches. */
export const UNITY_ORG_ROOT_LOCATION_ID = "6943634bb4b9b2fe39890d5e";

/** Operational site id for Unity - Flemington (client folders + demo triage). */
export const UNITY_FLEMINGTON_LOCATION_ID = "6943634bb4b9b2fe39890d60";

export const UNITY_KNOWN_SITE_LOCATION_IDS = new Set([
  UNITY_FLEMINGTON_LOCATION_ID, // Unity - Flemington
  "6943634bb4b9b2fe39890d62", // Unity - Princeton
  UNITY_ORG_ROOT_LOCATION_ID, // Unity (corporate parent — dual-site umbrella only)
]);

export const UNITY_DUAL_SITE_LOCATION_IDS = UNITY_KNOWN_SITE_LOCATION_IDS;

export function normalizeLocationId(location) {
  return String(location?.id || location?._id || "").trim();
}

export function resolveCompanyId(location) {
  if (!location) return "";
  const raw = location.companyId?._id || location.companyId?.id || location.companyId;
  return raw != null ? String(raw).trim() : "";
}

/** Operational sites that hold client folders (Flemington + Princeton). */
export function isUnityOperationalSite(location) {
  if (!location) return false;
  const id = normalizeLocationId(location);
  if (id === "6943634bb4b9b2fe39890d60" || id === "6943634bb4b9b2fe39890d62") {
    return true;
  }
  const name = String(location.name || location.label || "").toLowerCase();
  if (!name.includes("unity")) return false;
  return name.includes("flemington") || name.includes("princeton");
}

function isUnityCorporateParent(location) {
  if (!location) return false;
  const id = normalizeLocationId(location);
  if (id === UNITY_ORG_ROOT_LOCATION_ID) return true;
  const name = String(location.name || location.label || "")
    .trim()
    .toLowerCase();
  return name === "unity";
}

/** True when user should see merged Flemington + Princeton data. */
export function isUnityDualSiteLocation(location) {
  if (!location) return false;
  if (isUnityOperationalSite(location)) return true;
  if (isUnityCorporateParent(location)) return true;
  const id = normalizeLocationId(location);
  return UNITY_KNOWN_SITE_LOCATION_IDS.has(id);
}

/** Location ids used to load folders, AIP changes, and workflows (Flemington + Princeton). */
export function getUnityDualSiteLocationIds(activeLocation, locations = []) {
  if (!isUnityDualSiteLocation(activeLocation)) {
    const id = normalizeLocationId(activeLocation);
    return id ? [id] : [];
  }

  const companyId = resolveCompanyId(activeLocation);
  const ids = new Set();

  const consider = (loc) => {
    if (!loc) return;
    if (companyId && resolveCompanyId(loc) && resolveCompanyId(loc) !== companyId) {
      return;
    }
    if (!isUnityOperationalSite(loc)) return;
    const lid = normalizeLocationId(loc);
    if (lid) ids.add(lid);
  };

  (locations || []).forEach(consider);
  consider(activeLocation);

  if (ids.size < 2) {
    ids.add("6943634bb4b9b2fe39890d60");
    ids.add("6943634bb4b9b2fe39890d62");
  }

  return [...ids].filter((id) => id && id !== UNITY_ORG_ROOT_LOCATION_ID);
}

/** Load clients from entire Unity company when on Flemington or Princeton. */
export function shouldUseUnityCompanyClientScope(activeLocation) {
  return isUnityDualSiteLocation(activeLocation);
}

export function resolveClientFetchParams(activeLocation, locations = []) {
  const companyId = resolveCompanyId(activeLocation);
  const locationId = normalizeLocationId(activeLocation);

  if (shouldUseUnityCompanyClientScope(activeLocation) && companyId) {
    return {
      companyId,
      scope: "unity-company",
      locationIds: getUnityDualSiteLocationIds(activeLocation, locations),
    };
  }

  return {
    companyId: companyId || undefined,
    locationId,
    scope: "location",
    locationIds: locationId ? [locationId] : [],
  };
}
